// Submit handler for the bulk request modal — fans the (recipient × item)
// grid out into individual createAccessRequest payloads and sends them all
// to a single backend endpoint that loops through them server-side.
//
// Groups are expanded client-side using the `memberIds` returned by
// /members/access/recipients (already intersected with the manager's
// allowed-requestor set, so we never produce unauthorized rows).
//
// The pure expansion + dedup logic lives in ./payloads.ts so it can be
// unit-tested without React / react-query / localStorage.

import { useBulkCreateAccessRequests } from "state/members/access_v2/catalogs";
import { catalogLinkName } from "state/members/access_v2/catalogNames";
import { useFrequentRequests } from "state/members/frequentRequests";
import { draftIdsToSign } from "state/members/stepUp";
import {
  buildBulkPayloads,
  dedupeErrors,
  resolveItemCfg,
  type SubmitRequestArgs,
} from "./payloads";

export type { SubmitRequestArgs };

export const useSubmitRequest = () => {
  const { mutate, isLoading } = useBulkCreateAccessRequests();
  const { trackRequest } = useFrequentRequests();

  const submit = async (args: SubmitRequestArgs) => {
    const payloads = buildBulkPayloads(args);

    if (payloads.length === 0) {
      return { total: 0, failed: 0, errors: [] as string[], drafts: [] as string[] };
    }

    try {
      const response = await mutate({ requests: payloads });
      const errors = dedupeErrors(response.results ?? []);

      // Record each requested resource for the Home "Frequent Access" card.
      // Resource-level (the entry is about the resource, not the recipient);
      // the saved duration uses the first recipient's resolved config.
      if (response.succeeded > 0) {
        const recipientId = args.recipients[0]?.id ?? "";
        for (const item of args.items) {
          const { duration } = resolveItemCfg(recipientId, item.id, args);
          trackRequest({
            selectableResourceId: item.resource.selectableResource.id,
            catalogItemId: item.catalogItemId,
            catalogId: item.catalogId,
            catalogItemName: item.catalogItem.name,
            appName: item.catalogItem.name,
            appIcon: item.catalogItem.imageUrl,
            resourceName: catalogLinkName(item.resource),
            rawResourceName: item.resource.name,
            lastDuration: duration.indefinite
              ? undefined
              : { value: duration.num, unit: duration.unit },
            lastIndefinite: Boolean(duration.indefinite),
            lastComments: args.bulkReason || undefined,
          });
        }
      }

      return {
        total: response.total,
        failed: response.total - response.succeeded,
        errors,
        // Requests created as drafts: their policy wants the requester's
        // passkey signature before they go for approval.
        drafts: draftIdsToSign(response.results ?? []),
      };
    } catch {
      return {
        total: payloads.length,
        failed: payloads.length,
        errors: ["Network error, please retry."],
        drafts: [] as string[],
      };
    }
  };

  return { submit, isSubmitting: isLoading };
};
